import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { PageHeader, Card, Btn, Badge, useToast } from '../components/shared/UI';
import { useT } from '../i18n';
import { useAuth } from '../context/AuthContext';
import CurrencyToggle from '../components/shared/CurrencyToggle';
import { planPricing, billingCurrency, saveBillingCurrency } from '../utils/helpers';
import api from '../utils/api';

const fmtDate = d => d ? new Date(d).toLocaleDateString('en-MY', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtAmt = (amount, currency) =>
  `${String(currency || 'myr').toUpperCase()} ${Number(amount || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Subscription() {
  const { user, updateUser } = useAuth();
  const [params] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [billing, setBilling] = useState(null);
  const [busy, setBusy] = useState('');
  const [pickedCurrency, setPickedCurrency] = useState(null);
  const { show, ToastContainer } = useToast();
  const { t } = useT();

  useEffect(() => {
    api.get('/stripe/config').then(r => setConfig(r.data)).catch(() => {});
    api.get('/stripe/payments').then(r => setBilling(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (params.get('status') === 'success') show(t('subscription.activated'));
    if (params.get('status') === 'cancelled') show(t('subscription.checkoutCancelled'), 'warning');
  }, [params, show, t]);

  // Stripe's webhook can land a moment after the redirect back — poll until the plan flips to active.
  const justPaid = params.get('status') === 'success';
  useEffect(() => {
    if (!justPaid) return undefined;
    let tries = 0;
    const id = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await api.get('/auth/me');
        if (data.subscriptionStatus === 'active') {
          updateUser(data);
          clearInterval(id);
          // The webhook that activated the plan also wrote the payment row — show it
          api.get('/stripe/payments').then(r => setBilling(r.data)).catch(() => {});
        }
      } catch { /* keep polling */ }
      if (tries >= 10) clearInterval(id);
    }, 2000);
    return () => clearInterval(id);
  }, [justPaid]); // updateUser is re-created every render — deliberately not a dependency

  const checkout = async plan => {
    setBusy(plan);
    try {
      const { data } = await api.post('/stripe/checkout', { plan, currency: price.currency });
      window.location.href = data.url;
    } catch (err) {
      show(err.response?.data?.message || t('subscription.checkoutFailed'), 'error');
      setBusy('');
    }
  };

  const portal = async () => {
    setBusy('portal');
    try {
      const { data } = await api.post('/stripe/portal');
      window.location.href = data.url;
    } catch (err) {
      show(err.response?.data?.message || t('subscription.portalUnavailable'), 'error');
      setBusy('');
    }
  };

  const trialDaysLeft = user?.trialEndsAt
    ? Math.max(0, Math.ceil((new Date(user.trialEndsAt) - Date.now()) / 86400000))
    : 0;
  const isActive = user?.subscriptionStatus === 'active';
  const isTrialing = user?.subscriptionStatus === 'trialing';
  const isPastDue = user?.subscriptionStatus === 'past_due';

  // Checkout runs on the web only: inside the native app Stripe would return to
  // https://invio.my (a different origin than the WebView), so the app keeps Talk to Us.
  // past_due users fix their card in the billing portal instead of subscribing twice.
  const canCheckout = !isActive && !isPastDue && config?.configured && !Capacitor.isNativePlatform();
  const price = planPricing(config, pickedCurrency || billingCurrency(config));
  const pickCurrency = c => { setPickedCurrency(c); saveBillingCurrency(c); };

  return (
    <div>
      <ToastContainer />
      <PageHeader title={t('subscription.title')} subtitle={t('subscription.subtitle')} />

      {/* Current status */}
      <Card style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-subtle)', letterSpacing: '0.5px', marginBottom: 6 }}>{t('subscription.currentPlan')}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--heading)', textTransform: 'capitalize' }}>
                {user?.plan === 'trial' ? t('subscription.freeTrial') : user?.plan || t('subscription.none')}
              </h2>
              <Badge
                label={user?.subscriptionStatus}
                color={isActive ? '#10B981' : isTrialing ? '#2E75B6' : isPastDue ? '#EF4444' : '#94a3b8'}
              />
            </div>
            {isTrialing && (
              <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 8 }}>
                {t('subscription.trialEndsIn')} <b style={{ color: trialDaysLeft <= 5 ? 'var(--danger)' : 'var(--text)' }}>{t('subscription.days', { d: trialDaysLeft })}</b> ({fmtDate(user.trialEndsAt)})
              </p>
            )}
            {isActive && user?.currentPeriodEnd && (
              <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 8 }}>
                {t('subscription.renewsOn')} <b style={{ color: 'var(--text)' }}>{fmtDate(user.currentPeriodEnd)}</b>
              </p>
            )}
            {isPastDue && (
              <p style={{ fontSize: 13, color: 'var(--danger)', marginTop: 8 }}>
                {t('subscription.pastDueMsg')}
              </p>
            )}
          </div>

          {user?.stripeCustomerId && (
            <Btn variant="outline" onClick={portal} disabled={busy === 'portal'}>
              {busy === 'portal' ? t('subscription.opening') : t('subscription.manageBilling')}
            </Btn>
          )}
        </div>
      </Card>

      {/* Billing history — only shown once the user has at least one recorded charge */}
      {billing?.payments?.length > 0 && (
        <Card style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--heading)' }}>{t('subscription.billingHistory')}</h3>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {billing.subscribedSince && (
                <span>{t('subscription.subscribedSince')}: <b style={{ color: 'var(--text)' }}>{fmtDate(billing.subscribedSince)}</b></span>
              )}
              {billing.totalPaid?.length > 0 && (
                <span>{t('subscription.totalPaid')}: <b style={{ color: 'var(--text)' }}>{billing.totalPaid.map(x => fmtAmt(x.total, x.currency)).join(' · ')}</b></span>
              )}
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {[t('subscription.payDate'), t('subscription.payAmount'), t('subscription.payPlan'), t('subscription.payStatus'), ''].map((h, i) => (
                    <th key={i} style={{ textAlign: 'start', padding: '7px 10px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {billing.payments.map(p => (
                  <tr key={p.id} style={{ borderBottom: '1px solid var(--border-light)' }}>
                    <td style={payTd}>{fmtDate(p.paidAt)}</td>
                    <td style={{ ...payTd, fontWeight: 600, whiteSpace: 'nowrap', color: p.status === 'succeeded' ? 'var(--success)' : 'var(--danger)' }}>
                      {fmtAmt(p.amount, p.currency)}
                    </td>
                    <td style={payTd}>{p.plan === 'monthly' ? t('pricing.monthly') : p.plan === 'yearly' ? t('pricing.yearly') : '—'}</td>
                    <td style={payTd}>
                      <Badge label={p.status === 'succeeded' ? t('subscription.paySucceeded') : t('subscription.payFailed')}
                             color={p.status === 'succeeded' ? '#10B981' : '#EF4444'} />
                    </td>
                    <td style={payTd}>
                      {p.receiptUrl && (
                        <a href={p.receiptUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#2E75B6' }}>
                          {t('subscription.payReceipt')} ↗
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Plans */}
      {canCheckout && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--heading)' }}>
              {isTrialing ? t('subscription.upgradeAnytime') : t('subscription.choosePlan')}
            </h3>
            <CurrencyToggle currencies={price.currencies} value={price.currency} onChange={pickCurrency} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 16 }}>
            <PlanCard
              title={t('pricing.monthly')}
              price={price.monthly}
              cadence={t('pricing.perMonth')}
              features={['f1', 'f2', 'f3', 'f4'].map(k => t(`pricing.plan.monthly.${k}`))}
              cta={t('subscription.subscribeMonthly')}
              busyLabel={t('subscription.redirecting')}
              onClick={() => checkout('monthly')}
              busy={busy === 'monthly'}
              disabled={!!busy}
            />
            <PlanCard
              title={t('pricing.yearly')}
              price={price.yearly}
              cadence={t('pricing.perYear')}
              badge={price.savePct > 0 ? t('pricing.save', { p: price.savePct }) : null}
              features={['f1', 'f2', 'f3', 'f4'].map(k => t(`pricing.plan.yearly.${k}`, { amount: price.yearlyPerMonth }))}
              cta={t('subscription.subscribeYearly')}
              busyLabel={t('subscription.redirecting')}
              onClick={() => checkout('yearly')}
              busy={busy === 'yearly'}
              disabled={!!busy}
              highlighted
            />
          </div>
          <p style={{ fontSize: 12, color: 'var(--text-subtle)', textAlign: 'center', margin: '14px 0 24px' }}>
            🔒 {t('subscription.securePayment')}
          </p>
        </>
      )}

      {/* Talk to Us */}
      {!isActive && (
        <Card style={{ textAlign: 'center', padding: '32px 24px' }}>
          <h3 style={{ fontSize: 20, fontWeight: 700, color: 'var(--heading)', marginBottom: 8 }}>
            {canCheckout ? t('subscription.needCustom') : isTrialing ? t('subscription.readyToContinue') : t('subscription.getInTouch')}
          </h3>
          <p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 24, maxWidth: 480, margin: '0 auto 24px' }}>
            {t('subscription.tailorPlan')}
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center' }}>
            <a href="mailto:marketing@alaganu.com" style={{ textDecoration: 'none' }}>
              <Btn variant={canCheckout ? 'outline' : 'accent'}>✉ {t('subscription.talkToUs')}</Btn>
            </a>
            <a href="https://wa.me/60106650171" target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
              <Btn variant="outline">💬 {t('subscription.whatsapp')}</Btn>
            </a>
          </div>
        </Card>
      )}
    </div>
  );
}

const payTd = { padding: '9px 10px', color: 'var(--text)', verticalAlign: 'middle' };

function PlanCard({ title, price, cadence, badge, features, cta, busyLabel, onClick, busy, disabled, highlighted }) {
  return (
    <Card style={{
      border: highlighted ? '2px solid var(--accent)' : '1px solid var(--border)',
      position: 'relative',
    }}>
      {badge && (
        <div style={{
          position: 'absolute', top: -10, insetInlineEnd: 16,
          background: 'var(--accent)', color: '#1A3C6E',
          fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 12, textTransform: 'uppercase', letterSpacing: '0.5px',
        }}>{badge}</div>
      )}
      <h4 style={{ fontSize: 18, fontWeight: 700, color: 'var(--heading)', marginBottom: 8 }}>{title}</h4>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 16 }}>
        <span style={{ fontSize: 32, fontWeight: 800, color: 'var(--text)' }}>{price}</span>
        <span style={{ fontSize: 14, color: 'var(--text-muted)' }}>{cadence}</span>
      </div>
      <ul style={{ listStyle: 'none', padding: 0, marginBottom: 20 }}>
        {features.map(f => (
          <li key={f} style={{ fontSize: 13, color: 'var(--text-muted)', padding: '4px 0', display: 'flex', gap: 6 }}>
            <span style={{ color: 'var(--success)' }}>✓</span> {f}
          </li>
        ))}
      </ul>
      <Btn variant={highlighted ? 'accent' : 'primary'} onClick={onClick} disabled={busy || disabled} style={{ width: '100%', justifyContent: 'center' }}>
        {busy ? busyLabel : cta}
      </Btn>
    </Card>
  );
}
